import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  IconButton,
  Link,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SendIcon from "@mui/icons-material/Send";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import api from "../../utils/api";
import ViolationLetterTemplate from "./Letters/ViolationLetterTemplate";
import ViolationLetterTemplate2 from "./Letters/ViolationLetterTemplate2";
import useCompanyLogo from "../../utils/useCompanyLogo";
import { useClientConfig } from "../../utils/ClientConfigContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import html2pdf from "html2pdf.js";

const ViolationNoticeTab = ({
  member,
  violationDetails,
  violationType,
  currentUserId,
}) => {
  const logoUrl = useCompanyLogo();
  const { violationLetterTemplate } = useClientConfig();
  const ActiveLetterTemplate = violationLetterTemplate === "2" ? ViolationLetterTemplate2 : ViolationLetterTemplate;
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("primary");
  const [selectedPropertyAddress, setSelectedPropertyAddress] = useState("main");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("preview");
  const [editableContent, setEditableContent] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [recipientEmail, setRecipientEmail] = useState(member?.EmailAddress || "");
  const [rawTemplateContent, setRawTemplateContent] = useState("");

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagInputs, setTagInputs] = useState({});
  const [manualTags, setManualTags] = useState({});

  const [pdfPreview, setPdfPreview] = useState({
    open: false,
    src: "",
  });

  const letterRef = useRef();

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const formatHtmlAddress = (htmlAddress) => {
    if (!htmlAddress) return "";
    const plain = htmlAddress
      .replace(/<br\s*\/?>/gi, ", ")
      .replace(/\s+/g, " ")
      .trim();

    const parts = plain.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return htmlAddress;

    const street = parts[0];
    const cityStateZip = parts.slice(1).join(", ");

    const formattedCityStateZip = cityStateZip.replace(
      /,\s*([A-Za-z]{2})\s*,?\s*(\d{5}(?:-\d{4})?)?/,
      (match, state, zip) => `, ${state.toUpperCase()}${zip ? " " + zip : ""}`
    );

    return `${street}<br/>${formattedCityStateZip}`;
  };

  const formatAddressOneLine = (htmlAddress) => {
    if (!htmlAddress) return "";
    return htmlAddress.replace(/<br\s*\/?>/gi, ", ").replace(/\s+/g, " ").trim();
  };

  const addressToUse = useMemo(() => {
    return selectedAddress === "alternate" && member?.AlternateAddressHtml
      ? formatHtmlAddress(member.AlternateAddressHtml)
      : formatHtmlAddress(member?.FullAddressHtml || "");
  }, [selectedAddress, member?.AlternateAddressHtml, member?.FullAddressHtml]);

  const propertyAddressSource = useMemo(() => {
    return selectedPropertyAddress === "alternate" && member?.AlternateAddressHtml
      ? member.AlternateAddressHtml
      : member?.FullAddressHtml || "";
  }, [selectedPropertyAddress, member?.AlternateAddressHtml, member?.FullAddressHtml]);

  const propertyAddress = useMemo(
    () => formatAddressOneLine(propertyAddressSource),
    [propertyAddressSource]
  );

  const propertyAddressPlain = useMemo(() => {
    return propertyAddressSource?.replace(/<br\s*\/?>/gi, " ").replace(/\s+/g, " ").trim() || "";
  }, [propertyAddressSource]);

  const getDeliveryMethod = (tpl) => {
    if (!tpl) return "Delivered via US Mail and Email";
    switch (tpl.id) {
      case 1:
        return "Delivered via US Mail and Email";
      case 4:
        return "Delivered via Certified US Mail and Email";
      case 5:
        return "Delivered via Email";
      default:
        return "Delivered via Certified US Mail and Email";
    }
  };

  const baseTags = useMemo(
    () => ({
      RecipientName: `${member?.FirstName || ""} ${member?.LastName || ""}`.trim(),
      RecipientAddress: addressToUse,
      RecipientEmail: recipientEmail || "N/A",
      name: `${member?.FirstName || ""} ${member?.LastName || ""}`.trim(),
      address: addressToUse,
      property_address: propertyAddress,
      violation_article: violationDetails?.violation_article || "N/A",
      violation_rule: violationDetails?.violation_rule || "N/A",
      required_action: violationDetails?.resolution || "N/A",
      ViolationType: violationType || "N/A",
      ViolationID: violationDetails?.id ? `VLT-${violationDetails.id}` : "N/A",
      full_address_plain: propertyAddressPlain,
      ViolationDate: violationDetails?.violated_at
        ? new Date(violationDetails.violated_at).toLocaleDateString("en-US")
        : "N/A",
      cure_by: violationDetails?.cure_by
        ? new Date(violationDetails.cure_by).toLocaleDateString("en-US")
        : "N/A",
      days_to_cure: violationDetails?.days_to_cure ?? "N/A",
      ViolationComment: violationDetails?.comment || "N/A",
      ViolationStatus: violationDetails?.status || "N/A",
      ViolationResolution: violationDetails?.resolution || "N/A",
      PropertyID: violationDetails?.property_id || "N/A",
      AccountID: violationDetails?.account_id || "N/A",
      InspectionID: violationDetails?.inspection_id || "N/A",
      ExternalID: violationDetails?.external_id || "N/A",
      CreatedAt: violationDetails?.created_at
        ? new Date(violationDetails.created_at).toLocaleDateString("en-US")
        : "N/A",
      UpdatedAt: violationDetails?.updated_at
        ? new Date(violationDetails.updated_at).toLocaleDateString("en-US")
        : "N/A",
      date: new Date().toLocaleDateString("en-US"),
    }),
    [
      member?.FirstName,
      member?.LastName,
      addressToUse,
      recipientEmail,
      propertyAddress,
      propertyAddressPlain,
      violationType,
      violationDetails?.id,
      violationDetails?.violation_article,
      violationDetails?.violation_rule,
      violationDetails?.resolution,
      violationDetails?.violated_at,
      violationDetails?.cure_by,
      violationDetails?.days_to_cure,
      violationDetails?.comment,
      violationDetails?.status,
      violationDetails?.property_id,
      violationDetails?.account_id,
      violationDetails?.inspection_id,
      violationDetails?.external_id,
      violationDetails?.created_at,
      violationDetails?.updated_at,
    ]
  );

  const tags = useMemo(() => ({ ...baseTags, ...manualTags }), [baseTags, manualTags]);

  const applyTags = (html, tagMap) => {
    if (!html) return "";
    let output = html;
    const mapToUse = tagMap || tags;

    Object.entries(mapToUse).forEach(([key, value]) => {
      const safeValue =
        typeof value === "string" ? value.replace(/\n/g, "<br/>") : value;
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      output = output.replace(regex, safeValue ?? "");
    });

    return output;
  };

  const extractTags = (html) => {
    if (!html) return [];
    const regex = /{{\s*(.*?)\s*}}/g;
    const matches = [...html.matchAll(regex)];
    return [...new Set(matches.map((m) => m[1]))];
  };

  const allTagsUsed = extractTags(rawTemplateContent);

  const isEmptyValue = (val) => {
    if (val === null || val === undefined) return true;
    const s = String(val).trim();
    return s === "" || s.toLowerCase() === "n/a";
  };

  const missingTags = allTagsUsed.filter((tag) => {
    if (!(tag in tags)) return true;
    return isEmptyValue(tags[tag]);
  });

  const handleApplyManualTags = () => {
    if (!missingTags.length) {
      setTagModalOpen(false);
      return;
    }

    const updatedManualTags = { ...manualTags, ...tagInputs };
    setManualTags(updatedManualTags);

    setEditableContent((prev) =>
      applyTags(prev, { ...baseTags, ...updatedManualTags })
    );

    setTagModalOpen(false);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get("/violation-letter-templates");
        const data = res.data || [];
        setTemplates(data);

        if (data.length > 0) {
          const firstTpl = data[0];
          setSelectedTemplateId(firstTpl.id);
          setRawTemplateContent(firstTpl.content);
          setEditableContent(
            applyTags(firstTpl.content, { ...baseTags, ...manualTags })
          );
        }
      } catch (err) {
        console.error("Failed to load violation templates:", err);
      } finally {
        setLoading(false);
      }
    };

    if (violationDetails?.id) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [violationDetails?.id]);

  

  useEffect(() => {
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;

    setRawTemplateContent(tpl.content);
    setEditableContent(applyTags(tpl.content, { ...baseTags, ...manualTags }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTemplateId,
    templates.length,
    JSON.stringify(baseTags),
    JSON.stringify(manualTags),
  ]);

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) {
      setRawTemplateContent(tpl.content);
      setEditableContent(applyTags(tpl.content, { ...baseTags, ...manualTags }));
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const buildSubjectAndFilename = () => {
    const tplName = selectedTemplate?.name || "Violation Letter";
    const violationId = violationDetails?.id || "Letter";
    const subject = `${tplName} #${violationId}`;
    const filename = `${tplName.replace(/\s+/g, "_")}_VLT_${violationId}.pdf`;
    return { subject, filename };
  };

  const buildServerPDFUrl = (fileUrl) =>
    `${api.defaults.baseURL}/violationsletters/${violationDetails.id}/${encodeURIComponent(fileUrl)}`;

  const generateLetter = async () => {
    if (!letterRef.current) return null;

    const { subject, filename } = buildSubjectAndFilename();

    await Promise.all(
      Array.from(letterRef.current.querySelectorAll("img"))
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.onload = img.onerror = resolve;
            })
        )
    );

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    letterRef.current.style.maxWidth = "816px";
    letterRef.current.style.margin = "0 auto";
    letterRef.current.style.padding = "0.25in";
    letterRef.current.style.backgroundColor = "#ffffff";

    const pdfBlob = await html2pdf()
      .set({
        margin: [0.5, 0.5, 0.5, 0.5],
        filename,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          scrollY: 0,
          useCORS: true,
          backgroundColor: "#ffffff",
          letterRendering: true,
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
          compress: false,
        },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(letterRef.current)
      .outputPdf("blob");

    const uploadData = new FormData();
    uploadData.append("pdf", pdfBlob, filename);
    uploadData.append("violation_id", violationDetails?.id || "");
    uploadData.append("template_id", selectedTemplate?.id || "");
    uploadData.append("address_type", selectedAddress);
    uploadData.append("recipient", recipientEmail);
    uploadData.append("content", editableContent);

    const uploadResponse = await api.post(
      "/violations/upload-letter-pdf",
      uploadData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    const pdf_path = uploadResponse.data?.pdf_path;

    const formData = new FormData();
    formData.append("pdf", pdfBlob, filename);
    formData.append("recipient", recipientEmail);
    formData.append("cc", ccEmail || "");
    formData.append("subject", subject);
    formData.append("content", editableContent);
    formData.append("violation_id", violationDetails?.id || "");
    formData.append("template_type", selectedTemplate?.type || "");
    formData.append("template_id", selectedTemplate?.id || "");
    formData.append("address_type", selectedAddress);
    formData.append("recipientAddress", addressToUse);
    formData.append("pdf_path", pdf_path || "");

    return {
      pdfBlob,
      filename,
      subject,
      formData,
      pdf_path,
    };
  };

  const handleExportLetter = async () => {
    try {
      const { pdf_path } = await generateLetter();

      if (!pdf_path) {
        alert("PDF was not generated on the server.");
        return;
      }

      const res = await api.get(`/violations/${violationDetails.id}/letter-files`);

      if (!res.data?.length) {
        alert("PDF saved, but server returned no files.");
        return;
      }

      const latest = res.data[0];
      const fileId = latest.id;
      const serverPdfUrl = buildServerPDFUrl(latest.file_url);

      setPdfPreview({
        open: true,
        src: serverPdfUrl,
      });

      await api.put(`/violations/${violationDetails.id}/log-letter`, {
        action: "export",
        template_id: selectedTemplate?.id,
        address_type: selectedAddress,
        recipient: addressToUse,
        template_body: editableContent,
        pdf_path,
        sent_by: currentUserId,
        file_id: fileId,
      });
    } catch (err) {
      console.error("Error exporting letter:", err);
      alert("Failed to export letter");
    }
  };

  const handleEmailLetter = async () => {
    if (!recipientEmail) {
      alert("Please enter a recipient email before sending.");
      return;
    }

    try {
      const { formData, pdf_path } = await generateLetter();

      if (!formData) return;

      await api.post("/send-violation-letter", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await api.put(`/violations/${violationDetails?.id}/log-letter`, {
        action: "email",
        template_id: selectedTemplate?.id,
        address_type: selectedAddress,
        recipient: recipientEmail,
        pdf_path,
        template_body: editableContent,
        sent_by: currentUserId,
      });

      alert("Letter emailed and logged.");
    } catch (err) {
      console.error("Error sending letter:", err);
      alert("Failed to send letter");
    }
  };

  if (!member || !violationDetails) {
    return (
      <Typography variant="body2" color="error">
        Loading notice details...
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "220px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Typography variant="body2" color="warning.main">
        No violation templates found.
      </Typography>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Violation Letter
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Preview, edit, export, or email a generated notice for this violation.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ mb: 2.5, justifyContent: "space-between", alignItems: { md: "center" } }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<InfoOutlinedIcon />}
                label={`Template: ${selectedTemplate?.name || "N/A"}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Violation #${violationDetails?.id || "N/A"}`}
                variant="outlined"
                size="small"
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportLetter}
                sx={secondaryButtonSx}
              >
                Export Letter as PDF
              </Button>

              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleEmailLetter}
                sx={primaryButtonSx}
              >
                Email Letter
              </Button>

              <Button
                variant="outlined"
                color={missingTags.length ? "warning" : "primary"}
                onClick={() => setTagModalOpen(true)}
                sx={secondaryButtonSx}
              >
                Enter Missing Info{missingTags.length ? ` (${missingTags.length})` : ""}
              </Button>
            </Stack>
          </Stack>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Select Template"
              onChange={(e) => handleTemplateChange(e.target.value)}
              sx={textFieldSx}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {member?.AlternateAddress && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Mailing Address</InputLabel>
              <Select
                value={selectedAddress}
                label="Select Mailing Address"
                onChange={(e) => setSelectedAddress(e.target.value)}
                sx={textFieldSx}
              >
                <MenuItem value="primary">Primary: {member.FullAddress}</MenuItem>
                <MenuItem value="alternate">Alternate: {member.AlternateAddress}</MenuItem>
              </Select>
            </FormControl>
          )}

          {member?.AlternateAddress && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Property Address</InputLabel>
              <Select
                value={selectedPropertyAddress}
                label="Select Property Address"
                onChange={(e) => setSelectedPropertyAddress(e.target.value)}
                sx={textFieldSx}
              >
                <MenuItem value="main">Main: {member.FullAddress}</MenuItem>
                <MenuItem value="alternate">Alternate: {member.AlternateAddress}</MenuItem>
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            required
            label="Recipient Email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ ...textFieldSx, mb: 2 }}
            placeholder="Enter recipient email"
          />

          <TextField
            fullWidth
            label="CC (optional)"
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
            sx={{ ...textFieldSx, mb: 2 }}
            placeholder="cc1@email.com, cc2@email.com"
          />

          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(47,99,173,0.08)",
                "& .MuiToggleButton-root": {
                  px: 1.75,
                  border: "none",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#5f6b7a",
                },
                "& .Mui-selected": {
                  color: "#2f63ad !important",
                  backgroundColor: "rgba(47,99,173,0.08)",
                },
              }}
            >
              <ToggleButton value="preview">
                <PreviewIcon sx={{ mr: 1 }} />
                Preview
              </ToggleButton>
              <ToggleButton value="edit">
                <EditNoteIcon sx={{ mr: 1 }} />
                Edit HTML
              </ToggleButton>
              <ToggleButton value="rich">Rich Text</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === "preview" ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "14px",
                backgroundColor: "#fff",
                border: "1px solid rgba(47,99,173,0.08)",
                overflowX: "auto",
              }}
            >
              <div ref={letterRef}>
                <ActiveLetterTemplate
                  type={selectedTemplate?.type}
                  member={member}
                  violation={violationDetails}
                  date={new Date()}
                  content={editableContent}
                  formattedAddress={addressToUse}
                  deliveryMethod={getDeliveryMethod(selectedTemplate)}
                  logoUrl={logoUrl}
                />
              </div>
            </Paper>
          ) : viewMode === "edit" ? (
            <TextField
              fullWidth
              multiline
              minRows={20}
              variant="outlined"
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              sx={{ ...textFieldSx, mt: 1 }}
            />
          ) : (
            <Box sx={{ mt: 2 }}>
              <ReactQuill
                theme="snow"
                value={editableContent}
                onChange={(value) => setEditableContent(value)}
                style={{ height: "400px", marginBottom: "40px" }}
              />
            </Box>
          )}
        </Box>
      </Card>

      <Dialog
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Enter Required Information</DialogTitle>
        <DialogContent>
          {missingTags.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              No additional tags detected in this template.
            </Typography>
          ) : (
            missingTags.map((t) => (
              <TextField
                key={t}
                margin="dense"
                fullWidth
                label={t.replace(/_/g, " ")}
                value={tagInputs[t] || ""}
                onChange={(e) =>
                  setTagInputs((prev) => ({
                    ...prev,
                    [t]: e.target.value,
                  }))
                }
                sx={textFieldSx}
              />
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={() => setTagModalOpen(false)} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyManualTags}
            variant="contained"
            disabled={missingTags.length === 0}
            sx={primaryButtonSx}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfPreview.open}
        onClose={() => setPdfPreview({ open: false, src: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "black",
            p: 2,
          }}
        >
          <IconButton
            onClick={() => setPdfPreview({ open: false, src: "" })}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(0,0,0,0.4)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box sx={{ width: "100%", height: "90vh", maxWidth: "1200px" }}>
            <object
              data={pdfPreview.src}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <Typography color="white">
                PDF preview not supported.{" "}
                <Link href={pdfPreview.src} target="_blank" rel="noopener" sx={{ color: "#90caf9" }}>
                  Open PDF
                </Link>
              </Typography>
            </object>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              href={pdfPreview.src}
              target="_blank"
              download
              sx={primaryButtonSx}
            >
              Download PDF
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViolationNoticeTab;